import React, { useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Grid,
  Avatar,
  Chip,
  Card,
  CardContent,
  IconButton,
  Collapse,
  Tabs,
  Tab,
  LinearProgress,
  TextField,
  InputAdornment,
  Pagination
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Group as GroupIcon,
  Info as InfoIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

// 生成模拟数据（保持与UwstatisticsPage一致）
const generateBranchData = () => {
  const branches = [
    {
      id: 'all',
      name: 'All Branches',
      location: 'Company Wide',
      monthlyPolicies: 0,
      annualSubmit: 0,
      totalBrokers: 200,
      totalYoungDriver: 0,
      totalSingleAuto: 0,
      totalHighRisk: 0,
      totalHighQuality: 0,
      totalAuto: 0,
      totalHome: 0,
      totalCommercial: 0,
      totalPrem: 0,
      avgQualityScore: 0
    },
    {
      id: 'downtown',
      name: 'Downtown Branch',
      location: 'Vancouver Downtown',
      monthlyPolicies: 220,
      autoSubmit: 132,
      homeSubmit: 66,
      commercialSubmit: 22,
      annualSubmit: 2640,
      totalBrokers: 60
    },
    {
      id: 'richmond',
      name: 'Richmond Branch',
      location: 'Richmond Center',
      monthlyPolicies: 180,
      autoSubmit: 108,
      homeSubmit: 54,
      commercialSubmit: 18,
      annualSubmit: 2160,
      totalBrokers: 50
    },
    {
      id: 'burnaby',
      name: 'Burnaby Branch',
      location: 'Metrotown',
      monthlyPolicies: 170,
      autoSubmit: 102,
      homeSubmit: 51,
      commercialSubmit: 17,
      annualSubmit: 2040,
      totalBrokers: 50
    },
    {
      id: 'surrey',
      name: 'Surrey Branch',
      location: 'Surrey Central',
      monthlyPolicies: 150,
      autoSubmit: 90,
      homeSubmit: 45,
      commercialSubmit: 15,
      annualSubmit: 1800,
      totalBrokers: 40
    }
  ];

  return branches.map(branch => {
    if (branch.id === 'all') {
      return {
        ...branch,
        brokers: [],
        monthlyPolicies: 720,
        annualSubmit: 8640,
        totalYoungDriver: 150,
        totalSingleAuto: 360,
        totalHighRisk: 60,
        totalHighQuality: 280,
        totalAuto: 432,
        totalHome: 216,
        totalCommercial: 72,
        monthlyPrem: 140000,
        avgQualityScore: 96
      };
    }

    // 生成经纪人数据时添加annualSubmit
    const brokers = Array(branch.totalBrokers).fill(null).map((_, index) => {
      const baseMonthly = Math.floor(branch.monthlyPolicies / branch.totalBrokers);
      const monthlyPolicies = baseMonthly + Math.floor(Math.random() * 5);
      
      // 调整各险种保单数的计算方式，确保总和等于monthlyPolicies
      const autoSubmit = Math.floor(monthlyPolicies * 0.60);
      const homeSubmit = Math.floor(monthlyPolicies * 0.30);
      const commercialSubmit = monthlyPolicies - autoSubmit - homeSubmit; // 确保总和正确
      
      return {
        id: `${branch.id}-${index}`,
        name: `Broker ${index + 1}`,
        monthlyPolicies: monthlyPolicies,
        annualSubmit: monthlyPolicies * 12 + Math.floor(Math.random() * 24),
        autoSubmit: autoSubmit,
        homeSubmit: homeSubmit,
        commercialSubmit: commercialSubmit,
        highRiskDriver: Math.floor(monthlyPolicies * 0.08),
        highQualityInsured: Math.floor(monthlyPolicies * 0.40),
        monthlyPrem: (monthlyPolicies * 2333 * (0.9 + Math.random() * 0.3)).toFixed(2),
        youngDriverSubmit: Math.floor(monthlyPolicies * 0.20),
        singleAutoSubmit: Math.floor(monthlyPolicies * 0.50),
        qualityScore: Math.floor(88 + Math.random() * 9),
        branch: branch.name
      };
    });

    // 计算分行汇总数据
    const branchTotal = {
      ...branch,
      brokers,
      annualSubmit: brokers.reduce((sum, b) => sum + b.annualSubmit, 0),
      totalYoungDriver: brokers.reduce((sum, b) => sum + b.youngDriverSubmit, 0),
      totalSingleAuto: brokers.reduce((sum, b) => sum + b.singleAutoSubmit, 0),
      totalHighRisk: brokers.reduce((sum, b) => sum + b.highRiskDriver, 0),
      totalHighQuality: brokers.reduce((sum, b) => sum + b.highQualityInsured, 0),
      totalAuto: brokers.reduce((sum, b) => sum + b.autoSubmit, 0),
      totalHome: brokers.reduce((sum, b) => sum + b.homeSubmit, 0),
      totalCommercial: brokers.reduce((sum, b) => sum + b.commercialSubmit, 0),
      monthlyPrem: brokers.reduce((sum, b) => sum + parseFloat(b.monthlyPrem), 0),
      avgQualityScore: Math.floor(brokers.reduce((sum, b) => sum + b.qualityScore, 0) / brokers.length)
    };

    return branchTotal;
  });
};

const BrokerTable = ({ brokers = [] }) => (
  <TableContainer component={Paper} elevation={0} sx={{ 
    border: '1px solid #e0e0e0',
    borderRadius: 2,
    overflow: 'hidden'
  }}>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ bgcolor: '#f8f9fa' }}>
          <TableCell>Branch</TableCell>
          <TableCell>Broker</TableCell>
          <TableCell align="center" sx={{ fontWeight: 500 }}>Monthly</TableCell>
          <TableCell align="center" sx={{ fontWeight: 500 }}>Annual</TableCell>
          <TableCell align="center">Auto</TableCell>
          <TableCell align="center">Home</TableCell>
          <TableCell align="center">Commercial</TableCell>
          <TableCell align="center">Young</TableCell>
          <TableCell align="center">Single Auto</TableCell>
          <TableCell align="center">High Risk</TableCell>
          <TableCell align="center">High Quality</TableCell>
          <TableCell align="center">Prem($)</TableCell>
          <TableCell align="center">Quality</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {brokers.map((broker) => (
          <TableRow 
            key={broker.id} 
            hover 
            sx={{ 
              '&:hover': { bgcolor: '#f5f9ff' }
            }}
          >
            <TableCell>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#1976d2' }}>
                {broker.branch}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">
                {broker.name}
              </Typography>
            </TableCell>
            <TableCell align="center">{broker.monthlyPolicies}</TableCell>
            <TableCell align="center">{broker.annualSubmit}</TableCell>
            <TableCell align="center">{broker.autoSubmit}</TableCell>
            <TableCell align="center">{broker.homeSubmit}</TableCell>
            <TableCell align="center">{broker.commercialSubmit}</TableCell>
            <TableCell align="center">{broker.youngDriverSubmit}</TableCell>
            <TableCell align="center">{broker.singleAutoSubmit}</TableCell>
            <TableCell align="center">
              <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 500 }}>
                {broker.highRiskDriver}
              </Typography>
            </TableCell>
            <TableCell align="center">
              <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                {broker.highQualityInsured}
              </Typography>
            </TableCell>
            <TableCell align="center">
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                ${Number(broker.monthlyPrem).toLocaleString()}
              </Typography>
            </TableCell>
            <TableCell align="center">
              <Typography variant="body2" sx={{ 
                fontWeight: 500,
                color: broker.qualityScore >= 90 ? '#2e7d32' :
                        broker.qualityScore >= 80 ? '#ed6c02' : '#d32f2f'
              }}>
                {broker.qualityScore}%
              </Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// 新增分行统计表格组件
const BranchStatsTable = ({ branches }) => (
  <TableContainer component={Paper} elevation={0} sx={{ 
    border: '1px solid #e0e0e0',
    borderRadius: 2,
    overflow: 'hidden'
  }}>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ bgcolor: '#f8f9fa' }}>
          <TableCell>Branch</TableCell>
          <TableCell align="center">Monthly Submit</TableCell>
          <TableCell align="center">Annual Submit</TableCell>
          <TableCell align="center">Auto</TableCell>
          <TableCell align="center">Home</TableCell>
          <TableCell align="center">Commercial</TableCell>
          <TableCell align="center">Monthly Prem($)</TableCell>
          <TableCell align="center">Brokers</TableCell>
          <TableCell align="center">Avg Quality</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {branches.map((branch) => (
          <TableRow 
            key={branch.id} 
            hover
            sx={{ 
              '&:hover': { bgcolor: '#f5f9ff' }
            }}
          >
            <TableCell>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#1976d2' }}>
                {branch.name}
              </Typography>
            </TableCell>
            <TableCell align="center">{branch.monthlyPolicies}</TableCell>
            <TableCell align="center">{branch.annualSubmit}</TableCell>
            <TableCell align="center">
              {branch.id === 'all' ? branch.totalAuto : 
                Math.floor(branch.monthlyPolicies * 0.60)}
            </TableCell>
            <TableCell align="center">
              {branch.id === 'all' ? branch.totalHome : 
                Math.floor(branch.monthlyPolicies * 0.30)}
            </TableCell>
            <TableCell align="center">
              {branch.id === 'all' ? branch.totalCommercial : 
                branch.totalCommercial || Math.floor(branch.monthlyPolicies * 0.10)}
            </TableCell>
            <TableCell align="center">
              ${(branch.id === 'all' ? branch.monthlyPrem : 
                branch.monthlyPolicies * 2333).toLocaleString()}
            </TableCell>
            <TableCell align="center">{branch.totalBrokers}</TableCell>
            <TableCell align="center">
              <Typography 
                variant="body2"
                sx={{ 
                  color: 
                    branch.avgQualityScore >= 85 ? '#2e7d32' :
                    branch.avgQualityScore >= 75 ? '#ef6c00' : '#c62828'
                }}
              >
                {branch.avgQualityScore}%
              </Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const ITEMS_PER_PAGE = 5; // 每页显示5条记录

const BrokerQualityDashboard = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage] = useState(ITEMS_PER_PAGE);
  const [timeRange, setTimeRange] = useState('monthly'); // 'monthly' or 'annual'

  const branchesData = generateBranchData();
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [expanded, setExpanded] = useState(false);

  const currentBranch = useMemo(() => 
    branchesData.find(b => b.id === selectedBranch) || branchesData[0],
    [selectedBranch, branchesData]
  );

  // 处理搜索和分页
  const filteredBrokers = useMemo(() => {
    if (selectedBranch === 'all') {
      return branchesData
        .filter(b => b.id !== 'all')
        .flatMap(b => 
          (b.brokers || []).map(broker => ({
            ...broker,
            branch: b.name
          }))
        );
    }
    return (branchesData.find(b => b.id === selectedBranch)?.brokers || [])
      .filter(broker => 
        broker.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        broker.branch.toLowerCase().includes(searchTerm.toLowerCase())
      );
  }, [branchesData, selectedBranch, searchTerm]);

  const paginatedBrokers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredBrokers.slice(start, start + itemsPerPage);
  }, [currentPage, filteredBrokers, itemsPerPage]);

  // 生成收入数据示例
  const generateRevenueData = (branches, range) => {
    return branches.filter(b => b.id !== 'all').map(branch => ({
      name: branch.name,
      value: range === 'monthly' ? 
        branch.monthlyPrem * 0.85 : // 假设85%的月保费收入
        branch.monthlyPrem * 12 * 0.82 // 年收入估算
    }));
  };

  // 生成年龄分布数据
  const generateAgeData = (branches) => {
    const ageGroups = [
      { name: '18-25', value: 0 },
      { name: '26-35', value: 0 },
      { name: '36-50', value: 0 },
      { name: '51+', value: 0 }
    ];

    branches.filter(b => b.id !== 'all').forEach(branch => {
      branch.brokers.forEach(broker => {
        const age = Math.floor(18 + Math.random() * 40); // 模拟年龄分布
        if (age <= 25) ageGroups[0].value += 1;
        else if (age <= 35) ageGroups[1].value += 1;
        else if (age <= 50) ageGroups[2].value += 1;
        else ageGroups[3].value += 1;
      });
    });

    return ageGroups;
  };

  // 生成客户类型分布数据
  const generateClientTypeData = (branches) => {
    let totalAuto = 0;
    let totalHome = 0;
    let totalCommercial = 0;

    branches.filter(b => b.id !== 'all').forEach(branch => {
      totalAuto += branch.totalAuto;
      totalHome += branch.totalHome;
      totalCommercial += branch.totalCommercial;
    });

    return [
      { name: 'Auto', value: totalAuto },
      { name: 'Home', value: totalHome },
      { name: 'Commercial', value: totalCommercial },
    ];
  };

  const COLORS = [
    '#1976d2', // 蓝色
    '#4caf50', // 绿色
    '#ff9800', // 橙色
    '#9c27b0', // 紫色
    '#e91e63'  // 粉色
  ]; // 扩展为5种不重复颜色

  // 生成月度数据
  const generateMonthlyData = () => {
    return Array.from({ length: 12 }, (_, i) => ({
      month: new Date(2024, i).toLocaleString('en-US', { month: 'short' }),
      policies: 500 + Math.floor(Math.random() * 1500),
      premium: (500000 + Math.random() * 1500000) // 保持月度数据范围
    }));
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        {/* 分支选择标签 - 移动到最顶部 */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={selectedBranch}
            onChange={(_, newValue) => setSelectedBranch(newValue)}
            variant="scrollable"
          >
            {branchesData.map((branch) => (
              <Tab
                key={branch.id}
                label={branch.name}
                value={branch.id}
                sx={{ 
                  minWidth: 120,
                  fontSize: '0.875rem',
                  fontWeight: 500 
                }}
              />
            ))}
          </Tabs>
        </Box>

        <Typography variant="h4" sx={{ 
          color: '#1976d2', 
          mb: 2,
          fontWeight: 600,
          mt: 2  // 添加顶部间距
        }}>
          Broker Performance Dashboard
        </Typography>

        {/* 时间范围选择器 */}
        <Box sx={{ maxWidth: 200, mb: 4 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              label="Time Range"
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="annual">Annual</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* 饼图容器 */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 3, height: 400 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
                Revenue Distribution by Branch ({timeRange === 'monthly' ? 'Monthly' : 'Annual'})
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <PieChart>
                  <Pie
                    data={generateRevenueData(branchesData, timeRange)}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ percent }) => `${(percent * 100).toFixed(2)}%`}
                  >
                    {generateRevenueData(branchesData, timeRange).map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={COLORS[index % 5]} 
                      />
                    ))}
                  </Pie>
                  <Legend 
                    layout="vertical" 
                    verticalAlign="middle" 
                    align="right"
                  />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 3, height: 400 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
                Age Distribution of Policy Holders
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <PieChart>
                  <Pie
                    data={generateAgeData(branchesData)}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ percent }) => `${(percent * 100).toFixed(2)}%`}
                  >
                    {generateAgeData(branchesData).map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={COLORS[index % 5]} 
                      />
                    ))}
                  </Pie>
                  <Legend 
                    layout="vertical" 
                    verticalAlign="middle" 
                    align="right"
                  />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 3, height: 400 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
                Policy Type Distribution
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <PieChart>
                  <Pie
                    data={generateClientTypeData(branchesData)}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ percent }) => `${(percent * 100).toFixed(2)}%`}
                  >
                    {generateClientTypeData(branchesData).map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={COLORS[index % 5]} 
                      />
                    ))}
                  </Pie>
                  <Legend 
                    layout="vertical" 
                    verticalAlign="middle" 
                    align="right"
                  />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>

        {/* 年度数据柱状图 */}
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Performance Overview
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={generateMonthlyData()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                tick={{ fill: '#666' }}
              />
              <YAxis yAxisId="left" />
              <YAxis 
                yAxisId="right" 
                orientation="right"
                tickFormatter={(value) => `$${(value/1000).toFixed(0)}K`}
              />
              <Tooltip
                formatter={(value, name) => 
                  name === 'premium' 
                    ? [`$${value.toLocaleString()}`, 'Monthly Premium'] 
                    : [value, 'Policies Submitted']
                }
              />
              <Legend />
              <Bar 
                yAxisId="left"
                dataKey="policies" 
                name="Policies Submitted"
                fill="#1976d2"
                barSize={25}
              />
              <Bar
                yAxisId="right"
                dataKey="premium" 
                name="Monthly Premium"
                fill="#4caf50"
                barSize={25}
              />
            </BarChart>
          </ResponsiveContainer>
        </Paper>

        {/* 质量评分标准折叠面板 - 移动到柱状图下方 */}
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              '&:hover': { bgcolor: '#f5f9ff' }
            }}
            onClick={() => setExpanded(!expanded)}
          >
            <ExpandMoreIcon sx={{ 
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.3s',
              color: '#1976d2'
            }} />
            <Typography variant="h6" sx={{ 
              color: '#1976d2'
            }}>
              Quality Score Algorithm
            </Typography>
          </Box>
          
          <Collapse in={expanded}>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                {/* Key Definitions */}
                <Grid item xs={12}>
                  <Box sx={{ 
                    p: 2,
                    borderLeft: '4px solid #1976d2',
                    bgcolor: '#f8f9fa',
                    borderRadius: 1
                  }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      Key Definitions
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                      <li>
                        <Chip label="High Risk" size="small" color="error" variant="outlined" sx={{ mr: 1 }} />
                        Clients with at-fault accident within past 6 years
                      </li>
                      <li>
                        <Chip label="Young Driver" size="small" color="warning" variant="outlined" sx={{ mr: 1 }} />
                        Drivers under 25 years old
                      </li>
                      <li>
                        <Chip label="Single Auto" size="small" color="warning" variant="outlined" sx={{ mr: 1 }} />
                        Only auto insurance without property coverage
                      </li>
                      <li>
                        <Chip label="High Quality" size="small" color="success" variant="outlined" sx={{ mr: 1 }} />
                        Clients over 30 with both auto & property policies
                      </li>
                    </Box>
                  </Box>
                </Grid>

                {/* Portfolio Mix */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    p: 2,
                    borderLeft: '4px solid #4caf50',
                    bgcolor: '#f8f9fa',
                    borderRadius: 1
                  }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      Monthly Portfolio Mix
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                      <li>Monthly Property Policies ≥3: +20%</li>
                      <li>Monthly Commercial Policy ≥1: +15%</li>
                      <li>Single Auto Policies ≥5: -15%</li>
                      <li>Monthly Property:Auto Ratio 2:3: +10%</li>
                    </Box>
                  </Box>
                </Grid>

                {/* Risk Profile */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    p: 2,
                    borderLeft: '4px solid #ff9800',
                    bgcolor: '#f8f9fa',
                    borderRadius: 1
                  }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      Monthly Risk Profile
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                      <li>High Quality Clients ≥4/month: +15%</li>
                      <li>Young Drivers ≥3/month: -15%</li>
                      <li>High Risk Clients ≥2/month: -20%</li>
                      <li>Clean Risk Month (No HR): +10%</li>
                    </Box>
                  </Box>
                </Grid>

                {/* Premium Distribution */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    p: 2,
                    borderLeft: '4px solid #2196f3',
                    bgcolor: '#f8f9fa',
                    borderRadius: 1
                  }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      Monthly Premium Mix
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                      <li>Property Premium ≥$5,000/month: +15%</li>
                      <li>Commercial Premium ≥$2,000/month: +10%</li>
                      <li>Average Premium per Policy >$1,000: +5%</li>
                      <li>Total Monthly Premium >$10,000: +5%</li>
                    </Box>
                  </Box>
                </Grid>

                {/* Quality Metrics */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    p: 2,
                    borderLeft: '4px solid #9c27b0',
                    bgcolor: '#f8f9fa',
                    borderRadius: 1
                  }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      Monthly Quality Metrics
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                      <li>Multi-line Clients ≥3/month: +15%</li>
                      <li>Monthly Policies ≥8: +10%</li>
                      <li>New Business ≥2/month: +5%</li>
                      <li>No Policy Cancellation: +5%</li>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Final Score Calculation
                </Typography>
                <Box component="ol" sx={{ pl: 2, '& li': { mb: 1 } }}>
                  <li>Base Score = 70 points</li>
                  <li>Portfolio Mix Adjustment: -15% to +45%</li>
                  <li>Risk Profile Impact: -35% to +25%</li>
                  <li>Premium Distribution Bonus: 0% to +35%</li>
                  <li>Quality Metrics Addition: 0% to +35%</li>
                  <li>Final Score = Base + All Adjustments (capped at 100)</li>
                </Box>
              </Box>
            </Box>
          </Collapse>
        </Paper>

        {/* 关键指标卡片 */}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {[
            'monthlyPolicies', 
            'annualSubmit',
            'monthlyPrem',
            'totalBrokers',
            'totalYoungDriver',
            'totalSingleAuto', 
            'totalHighRisk',
            'totalHighQuality',
            'totalAuto',
            'totalHome',
            'totalCommercial',
            'avgQualityScore'
          ].map((metric, index) => (
            <Grid item xs={6} sm={4} md={2} key={metric}>
              <Card sx={{ 
                height: '100%', 
                p: 0.5,
                '&:hover': { boxShadow: 1 }
              }}>
                <CardContent sx={{ 
                  p: 1, 
                  '&:last-child': { pb: 1 } 
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    flexDirection: 'column',
                    textAlign: 'center'
                  }}>
                    <Typography variant="caption" color="textSecondary" sx={{ 
                      lineHeight: 1.2,
                      mb: 0.5,
                      fontSize: '0.75rem'
                    }}>
                      {metric === 'monthlyPolicies' && 'Monthly'}
                      {metric === 'annualSubmit' && 'Annual'}
                      {metric === 'monthlyPrem' && 'Monthly Prem($)'}
                      {metric === 'totalBrokers' && 'Brokers'}
                      {metric === 'totalYoungDriver' && 'Y.Driver'}
                      {metric === 'totalSingleAuto' && 'S.Auto'}
                      {metric === 'totalHighRisk' && 'High Risk'}
                      {metric === 'totalHighQuality' && 'High Quality'}
                      {metric === 'totalAuto' && 'Auto'}
                      {metric === 'totalHome' && 'Home'}
                      {metric === 'totalCommercial' && 'Commercial'}
                      {metric === 'avgQualityScore' && 'Quality'}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 600,
                      fontSize: '1rem'
                    }}>
                      {metric === 'monthlyPrem' ? 
                        `$${(currentBranch[metric] || 0).toLocaleString(undefined, { 
                          maximumFractionDigits: 0 
                        })}` : 
                        currentBranch[metric] || 0}
                      {['avgQualityScore'].includes(metric) && (
                        <span style={{ 
                          fontSize: '0.75em', 
                          color: '#666', 
                          marginLeft: '2px' 
                        }}>%</span>
                      )}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* 搜索和分页控件 */}
        <Box sx={{ 
          mb: 3, 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: '#f8f9fa',
          p: 2,
          borderRadius: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ mr: 2, color: '#1976d2', fontWeight: 500 }}>
              {selectedBranch === 'all' ? 'All Brokers' : `${currentBranch.name} Brokers`}
            </Typography>
            <TextField
              size="small"
              placeholder="Search by broker name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#9e9e9e' }} />
                  </InputAdornment>
                ),
                sx: {
                  bgcolor: '#ffffff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2'
                  }
                }
              }}
              sx={{ width: 250 }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" color="textSecondary" sx={{ mr: 2 }}>
              Total Results: {filteredBrokers.length}
            </Typography>
            <Pagination
              count={Math.ceil(filteredBrokers.length / itemsPerPage)}
              page={currentPage}
              onChange={(_, value) => setCurrentPage(value)}
              color="primary"
              size="small"
              sx={{
                '& .MuiPaginationItem-root': {
                  bgcolor: '#ffffff'
                }
              }}
            />
          </Box>
        </Box>

        {/* 搜索结果为空时的提示 */}
        {filteredBrokers.length === 0 && (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            bgcolor: '#f8f9fa',
            borderRadius: 1
          }}>
            <Typography color="textSecondary">
              No brokers found matching your search criteria
            </Typography>
          </Box>
        )}

        {/* 修改全行视图的渲染逻辑 */}
        {selectedBranch === 'all' ? (
          <Box>
            <BranchStatsTable 
              branches={branchesData.filter(b => 
                b.id !== 'all' && 
                ['downtown', 'richmond', 'burnaby', 'surrey'].includes(b.id)
              )} 
            />
          </Box>
        ) : (
          <Box>
            <BrokerTable brokers={paginatedBrokers} />
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default BrokerQualityDashboard; 